import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { GRAMS_PER_OZ, normalizeDetailAmounts, sumDetailAmountsForColor, sumAllDetailAmounts } from './calculatorUtils';
import { weightToDisplay, weightUnitLabel, lengthToDisplay, lengthUnitLabel, areaToDisplay, areaUnitLabel } from './unitConversion';

// Extra per-color icing categories shown below the main Flood totals.
const DETAIL_ROW_CONFIG = [
  { key: 'detail', label: 'Detail' },
  { key: 'floral', label: 'Floral' },
  { key: 'other', label: 'Other' },
];

async function imageToBase64(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
}

function buildResultRows({ cookieRows, dimensionsRows, orderRows, setRows, colorNames, useSets, calculateColorIcing }) {
  const resultRows = [];
  const colorTotals = Array(colorNames.length).fill(0);
  let grandTotal = 0;

  if (useSets) {
    const setGroups = {};
    setRows.forEach(setRow => {
      if (!setRow.cookieId || !setRow.setName) return;
      if (!setGroups[setRow.setName]) setGroups[setRow.setName] = [];
      setGroups[setRow.setName].push(setRow);
    });
    Object.entries(setGroups).forEach(([setName, rows]) => {
      rows.forEach(setRow => {
        const cookie = cookieRows.find(c => c.id === setRow.cookieId);
        if (!cookie) return;
        const colorAmounts = calculateColorIcing(setRow.cookieId, setRow.count);
        let rowTotal = 0;
        colorAmounts.forEach((amount, i) => { rowTotal += amount; colorTotals[i] += amount; });
        grandTotal += rowTotal;
        resultRows.push({ setName, cookieId: setRow.cookieId, cookieName: cookie.cookieName, colorAmounts, total: rowTotal });
      });
    });
  } else {
    cookieRows.forEach(cookie => {
      const orderRow = orderRows.find(o => o.cookieId === cookie.id);
      const count = orderRow ? Number(orderRow.count) : 0;
      const colorAmounts = calculateColorIcing(cookie.id, count);
      let rowTotal = 0;
      colorAmounts.forEach((amount, i) => { rowTotal += amount; colorTotals[i] += amount; });
      grandTotal += rowTotal;
      resultRows.push({ cookieId: cookie.id, cookieName: cookie.cookieName, count, colorAmounts, total: rowTotal });
    });
  }

  return { resultRows, colorTotals, grandTotal };
}

// Helper: format cookie dimensions into a compact string for PDF cells
function formatDimsCell(cookieId, dimensionsRows, isMetric) {
  if (!dimensionsRows) return '';
  const dim = dimensionsRows.find(d => d.cookieId === cookieId);
  if (!dim || !dim.shapeType) return '';
  const lbl = lengthUnitLabel(isMetric);
  const shape = dim.shapeType.charAt(0).toUpperCase() + dim.shapeType.slice(1);
  if (dim.shapeType === 'circle') {
    const d = lengthToDisplay(dim.diameter, isMetric);
    return d ? `${shape} ${d} ${lbl}` : shape;
  }
  if (dim.shapeType === 'square') {
    const s = lengthToDisplay(dim.width, isMetric);
    return s ? `${shape} ${s}x${s} ${lbl}` : shape;
  }
  if (dim.shapeType === 'override' || dim.shapeType === 'photo') {
    const a = dim.overrideArea ? areaToDisplay(dim.overrideArea, isMetric) : null;
    return a ? `${shape}\n${a} ${areaUnitLabel(isMetric)}` : shape;
  }
  const w = lengthToDisplay(dim.width, isMetric);
  const l = lengthToDisplay(dim.length, isMetric);
  return (w && l) ? `${shape} ${w}x${l} ${lbl}` : shape;
}

export async function exportToPdf({
  cookieRows,
  dimensionsRows,
  orderRows,
  setRows,
  colorNames,
  useSets,
  calculateColorIcing,
  loadedPlanName,
  isMetric = false,
  isMetricWeight = true,
  detailAmounts,
}) {
  const normalizedDetailAmounts = normalizeDetailAmounts(detailAmounts, colorNames.length);
  const logoB64 = await imageToBase64(`${process.env.PUBLIC_URL}/images/Icing Calc Logo large.png`);

  // Switch to landscape when there are many colors (>5) to give columns breathing room
  const useLandscape = colorNames.length > 5;
  const doc = new jsPDF({ orientation: useLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'letter' });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 16;
  const marginR = 16;
  const contentW = pageW - marginL - marginR;

  const BLACK = [0,   0,   0];
  const DARK  = [40,  40,  50];
  const GREY  = [110, 110, 120];
  const WHITE = [255, 255, 255];
  const RULE  = [180, 180, 180];

  // ── Header ─────────────────────────────────────────────────────────
  let cursorY = 14;
  const logoW = 24;
  const logoH = 24;

  if (logoB64) {
    try {
      doc.addImage(logoB64, 'PNG', marginL, cursorY - 8, logoW, logoH);
    } catch (_) { /* skip */ }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...BLACK);
  doc.text('The Icing Calculator', marginL + logoW + 4, cursorY + 2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GREY);
  doc.text('theicingcalculator.com', marginL + logoW + 4, cursorY + 8);

  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.setFontSize(8);
  doc.text(dateStr, pageW - marginR, cursorY + 2, { align: 'right' });

  cursorY += logoH - 4;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.5);
  doc.line(marginL, cursorY, pageW - marginR, cursorY);

  cursorY += 7;

  // ── Plan title ─────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...DARK);
  doc.text('Icing Plan', marginL, cursorY);

  if (loadedPlanName) {
    const titleWidth = doc.getTextWidth('Icing Plan');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...GREY);
    doc.text(loadedPlanName, marginL + titleWidth + 4, cursorY);
  }

  cursorY += 7;

  // ── Build data ─────────────────────────────────────────────────────
  const { resultRows, colorTotals, grandTotal } = buildResultRows({
    cookieRows, dimensionsRows, orderRows, setRows, colorNames, useSets, calculateColorIcing,
  });

  // ── Table ──────────────────────────────────────────────────────────
  const pdfWLabel = weightUnitLabel(isMetricWeight);
  const pdfWLabel2 = weightUnitLabel(!isMetricWeight);

  // Header: [Set?] | Cookie Name | How Many | Dimensions | ...colors... | Total (primary) | Total (alt)
  const tableHead = [];
  if (useSets) tableHead.push('Set');
  tableHead.push('Cookie Name', 'Qty', 'Dims');
  colorNames.forEach(c => tableHead.push(c));
  tableHead.push(`Total (${pdfWLabel})`);

  // rowRoles tracks each tableBody row's purpose in parallel, so cell coloring
  // (below, in didParseCell) never has to guess rows by counting from the end —
  // that offset-based approach broke whenever the number of summary rows changed.
  const tableBody = [];
  const rowRoles = [];

  resultRows.forEach(row => {
    const cells = [];
    if (useSets) cells.push(row.setName);
    cells.push(row.cookieName);
    cells.push(row.count != null ? String(row.count) : '');
    cells.push(formatDimsCell(row.cookieId, dimensionsRows, isMetric));
    row.colorAmounts.forEach(a => cells.push(`${weightToDisplay(a, isMetricWeight).toFixed(2)}`));
    cells.push(`${weightToDisplay(row.total, isMetricWeight).toFixed(2)}`);
    tableBody.push(cells);
    rowRoles.push('data');
  });

  // Total Flood row
  const totalsRow = [];
  if (useSets) totalsRow.push('');
  totalsRow.push(`Total Flood (${pdfWLabel})`, '', ''); // name + qty + dims placeholders
  colorTotals.forEach(t => totalsRow.push(`${weightToDisplay(t, isMetricWeight).toFixed(2)}`));
  totalsRow.push(`${weightToDisplay(grandTotal, isMetricWeight).toFixed(2)}`);
  tableBody.push(totalsRow);
  rowRoles.push('totalFlood');

  // Detail / Floral / Other rows
  DETAIL_ROW_CONFIG.forEach(({ key, label }) => {
    const rowSumGrams = (normalizedDetailAmounts[key] || []).reduce((s, g) => s + (Number(g) || 0), 0);
    const row = [];
    if (useSets) row.push('');
    row.push(label, '', '');
    colorNames.forEach((_, i) => {
      const dg = Number(normalizedDetailAmounts[key]?.[i]) || 0;
      row.push(`${weightToDisplay(dg, isMetricWeight).toFixed(2)}`);
    });
    row.push(`${weightToDisplay(rowSumGrams, isMetricWeight).toFixed(2)}`);
    tableBody.push(row);
    rowRoles.push('detailCategory');
  });

  // Flood + Extras row
  const pdfDetailSumGrams = sumAllDetailAmounts(normalizedDetailAmounts);
  const pdfGrandPlusDetail = grandTotal + pdfDetailSumGrams;
  const tpdRow = [];
  if (useSets) tpdRow.push('');
  tpdRow.push(`Flood+Extras (${pdfWLabel})`, '', '');
  colorTotals.forEach((t, i) => {
    const combined = t + sumDetailAmountsForColor(normalizedDetailAmounts, i);
    tpdRow.push(`${weightToDisplay(combined, isMetricWeight).toFixed(2)}`);
  });
  tpdRow.push(`${weightToDisplay(pdfGrandPlusDetail, isMetricWeight).toFixed(2)}`);
  tableBody.push(tpdRow);
  rowRoles.push('floodPlusExtras');

  // ── Column widths & font sizing ────────────────────────────────────
  const fixedSetColW   = useSets ? 22 : 0;
  const nameColW       = 28;
  const qtyColW        = 10;
  const dimsColW       = 24;
  const totalColW      = 20;
  const fixedWidths    = fixedSetColW + nameColW + qtyColW + dimsColW + totalColW;
  const remaining      = contentW - fixedWidths;
  const colorColW      = colorNames.length > 0 ? Math.max(14, remaining / colorNames.length) : remaining;

  // Adaptive font size: shrink for many columns
  let fontSize;
  if (colorNames.length > 8)      fontSize = 6;
  else if (colorNames.length > 6) fontSize = 6.5;
  else if (colorNames.length > 4) fontSize = 7;
  else                             fontSize = 8.5;

  const columnStyles = {};
  let colIdx = 0;
  if (useSets) { columnStyles[colIdx] = { cellWidth: fixedSetColW }; colIdx++; }
  columnStyles[colIdx] = { cellWidth: nameColW, fontStyle: 'bold' }; colIdx++;
  columnStyles[colIdx] = { cellWidth: qtyColW, halign: 'center' }; colIdx++;
  columnStyles[colIdx] = { cellWidth: dimsColW, halign: 'left', fontStyle: 'italic' }; colIdx++;
  colorNames.forEach(() => { columnStyles[colIdx] = { cellWidth: colorColW, halign: 'center' }; colIdx++; });
  columnStyles[colIdx] = { cellWidth: totalColW, halign: 'center', fontStyle: 'bold' };

  autoTable(doc, {
    startY: cursorY,
    head: [tableHead],
    body: tableBody,
    margin: { left: marginL, right: marginR },
    styles: {
      fontSize,
      cellPadding: 2,
      overflow: 'linebreak',
      textColor: DARK,
      fillColor: WHITE,
      lineColor: RULE,
      lineWidth: 0.4,
    },
    headStyles: {
      fillColor: WHITE,
      textColor: BLACK,
      fontStyle: 'bold',
      halign: 'center',
      fontSize,
      lineColor: RULE,
      lineWidth: 0.4,
    },
    didParseCell(data) {
      if (data.section === 'head') {
        data.cell.styles.fillColor = [217, 217, 217];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.textColor = DARK;
      } else if (data.section === 'body') {
        const role = rowRoles[data.row.index] || 'data';
        if (role === 'data') {
          data.cell.styles.fillColor = [255, 255, 255];
        } else if (role === 'totalFlood') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = DARK;
          data.cell.styles.fillColor = [217, 217, 217];
        } else if (role === 'detailCategory') {
          data.cell.styles.fontStyle = 'italic';
          data.cell.styles.textColor = DARK;
          data.cell.styles.fillColor = [214, 237, 214];
        } else if (role === 'floodPlusExtras') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = DARK;
          data.cell.styles.fillColor = [201, 222, 255];
        }
      }
    },
    columnStyles,
    tableWidth: contentW,
  });

  // ── Color Summary ──────────────────────────────────────────────────
  const summaryY = doc.lastAutoTable.finalY + 9;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  doc.text('Color Summary', marginL, summaryY);

  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.4);
  doc.line(marginL, summaryY + 1.5, pageW - marginR, summaryY + 1.5);

  const colsPerRow = useLandscape ? 4 : 3;
  const colW = contentW / colsPerRow;
  let sy = summaryY + 7;

  colorNames.forEach((color, i) => {
    const grams  = (colorTotals[i] ?? 0) + sumDetailAmountsForColor(normalizedDetailAmounts, i);
    const primaryVal = weightToDisplay(grams, isMetricWeight).toFixed(2);
    const col    = i % colsPerRow;
    const row    = Math.floor(i / colsPerRow);
    const x = marginL + col * colW;
    const y = sy + row * 7;

    doc.setFillColor(...GREY);
    doc.circle(x + 1.5, y - 1.5, 1.2, 'F');

    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(`${color}:`, x + 4.5, y);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...DARK);
    const labelW = doc.getTextWidth(`${color}: `);
    doc.text(`${primaryVal} ${pdfWLabel}`, x + 4.5 + labelW, y);
  });

  // ── Footer ─────────────────────────────────────────────────────────
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.4);
  doc.line(marginL, pageH - 14, pageW - marginR, pageH - 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY);
  doc.text('Generated by The Icing Calculator · theicingcalculator.com', pageW / 2, pageH - 9, { align: 'center' });


  // ── Save ───────────────────────────────────────────────────────────
  const date = new Date().toISOString().split('T')[0];
  const name = loadedPlanName ? loadedPlanName.replace(/[^a-z0-9]/gi, '_') : 'Icing_Plan';
  doc.save(`${name}_${date}.pdf`);
}
